import { useCallback } from 'react';
import { delay } from '@croquiscom/monolith';
import { useBottomSheet } from '@croquiscom-pvt/zds';

import { isZigzag } from '@common/device-manager';
import { sendSizeInfoMessage } from '@widgets/size-broadcast-channel';
import { PopupViewType, usePopupView } from '@domains/pdp-common';

import { AgreementBottomSheet, AgreementType } from '../components/actual-size/AgreementBottomSheet';
import { usePendingSizeInput } from '../queries/usePendingSizeInputQuery';

interface OpenPopupOptions {
  type?: AgreementType;
  /** true면 앱에서도 웹 바텀시트 사용 (기본값: false → 앱에서는 네이티브 팝업 사용) */
  useWebBottomSheet?: boolean;
}

export const useAgreementPopup = (catalogProductId: string) => {
  const { clearPending } = usePendingSizeInput();
  const { showPopup } = usePopupView();
  const { open, close } = useBottomSheet('custom');

  const openSizeRecommendationPopup = useCallback(
    async (options?: OpenPopupOptions) => {
      const useNativePopup = isZigzag() && !options?.useWebBottomSheet;

      if (useNativePopup) {
        await delay(300);
        showPopup(PopupViewType.SIZE_INFO_AGREEMENT, undefined, {
          agreement_type: options?.type ?? 'recommend',
        });
      } else {
        const agreementType = options?.type ?? 'recommend';
        open({
          children: (
            <AgreementBottomSheet
              catalogProductId={catalogProductId}
              type={options?.type}
              onClose={() => {
                clearPending();
                close();
              }}
              onAgree={() => {
                sendSizeInfoMessage({
                  action: 'AGREE_SIZE_INFO',
                  payload: { is_agreed: true, type: agreementType },
                });
                close();
              }}
            />
          ),
          onClose: () => {
            clearPending();
          },
        });
      }
    },
    [catalogProductId, clearPending, showPopup, open, close],
  );

  return {
    openSizeRecommendationPopup,
  };
};
